import { Link } from "react-router-dom";
import { Phone, MapPin, ChevronRight, Home, Bath, UtensilsCrossed, Hammer } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOMeta from "@/components/SEOMeta";
import LocalBusinessSchema from "@/components/LocalBusinessSchema";
import FAQ from "@/components/FAQ";

const faqData = [
  {
    question: "How long does a renovation take?",
    answer: "Kitchen renovations typically take 3-6 weeks, bathrooms 2-4 weeks, and basement finishing 4-8 weeks. We provide detailed timelines during planning and keep you updated throughout the project."
  },
  {
    question: "Do I need permits for my renovation?",
    answer: "Most structural changes, additions, and major electrical/plumbing work require permits. We handle all permit applications and ensure your renovation meets Ontario Building Code requirements."
  },
  {
    question: "Can I live in my home during renovation?",
    answer: "Many homeowners stay during renovations. We section off work areas, maintain clean access routes, and coordinate schedules to minimize disruption to your daily life."
  },
  {
    question: "What's included in your renovation service?",
    answer: "We provide full-service renovations including design consultation, permits, demolition, all trades coordination, material sourcing, and final finishing. One team manages your entire project from start to finish."
  }
];

const renovationServices = [
  {
    title: "Kitchen Renovations",
    description: "Complete kitchen remodels from layout redesign to custom cabinetry and countertop installation.",
    icon: UtensilsCrossed,
    features: ["Custom cabinets", "Countertops", "Flooring", "Lighting & electrical"]
  },
  {
    title: "Bathroom Remodeling",
    description: "Transform bathrooms with modern fixtures, tile work, and efficient layouts.",
    icon: Bath,
    features: ["Shower & tub", "Vanities", "Tile work", "Plumbing upgrades"]
  },
  {
    title: "Basement Finishing",
    description: "Turn unfinished basements into beautiful living spaces with proper moisture control.",
    icon: Home,
    features: ["Framing & drywall", "Flooring", "Bathrooms", "Rec rooms"]
  },
  {
    title: "Home Additions",
    description: "Expand your living space with room additions, second stories, and bump-outs.",
    icon: Hammer,
    features: ["Room additions", "Second stories", "Sunrooms", "Garage conversions"]
  }
];

const serviceAreas = [
  { name: "Niagara Region", link: "/locations" },
  { name: "Hamilton", link: "/locations/hamilton" },
  { name: "Toronto & GTA", link: "/locations/toronto" },
  { name: "Mississauga", link: "/locations/mississauga" }
];

export default function RenovationsServices() {
  return (
    <>
      <SEOMeta
        title="Home Renovation & Remodeling Contractor | Niagara • Hamilton • GTA"
        description="Complete home and commercial renovations across Niagara, Hamilton & the GTA. Kitchens, bathrooms, basements and full remodels."
        keywords="renovation contractor Niagara, Hamilton home remodel, GTA renovation company, kitchen renovation, bathroom remodel, basement finishing, additions"
        canonicalUrl="https://blueknightcontracting.ca/services/renovations"
      />
      <LocalBusinessSchema
        locationName="Renovation Services"
        addressLocality="Niagara Falls"
        addressRegion="ON"
        description="Professional home renovation and remodeling services"
        services={["Kitchen Renovation", "Bathroom Remodel", "Basement Finishing", "Home Additions"]}
      />
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        <section className="relative py-20 px-4 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Home Renovation & Remodeling
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Transform your space across Niagara, Hamilton & the GTA
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button asChild size="lg" className="gap-2">
                <Link to="/contact">Get Free Quote <ChevronRight className="h-5 w-5" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="gap-2">
                <a href="tel:+12899370797"><Phone className="h-5 w-5" /> (289) 937-0797</a>
              </Button>
            </div>
          </div>
        </section>

        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Renovation Services</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                From concept to completion, we manage every detail of your renovation project
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {renovationServices.map((service, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle>{service.title}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <ChevronRight className="h-4 w-4 text-primary" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Service Areas</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {serviceAreas.map((area, index) => (
                <Link key={index} to={area.link}>
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer">
                    <CardHeader>
                      <MapPin className="h-8 w-8 text-primary mb-2" />
                      <CardTitle className="text-lg">{area.name}</CardTitle>
                    </CardHeader>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <FAQ faqs={faqData} />
          </div>
        </section>

        <section className="py-16 px-4 bg-muted/30">
          <div className="max-w-4xl mx-auto text-center">
            <Card className="bg-gradient-to-br from-primary/10 to-accent/10">
              <CardHeader>
                <CardTitle className="text-3xl mb-4">Start Your Renovation</CardTitle>
                <CardDescription className="text-lg mb-6">
                  Contact us for a consultation and detailed renovation quote
                </CardDescription>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild size="lg"><Link to="/contact">Get Free Quote</Link></Button>
                  <Button asChild size="lg" variant="outline"><a href="tel:+12899370797">(289) 937-0797</a></Button>
                </div>
              </CardHeader>
            </Card>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
